/**
 * Media library functionality - handling media files, listeners, and callbacks.
 * Provides classes and functions for media management including upload, selection, and display.
 */

import { GqlClient, type GqlError, type GqlResponse } from './gql/GqlClient.js';
import { UPLOAD_FAILURE_REASONS, escapeHtml, webdavFailureMessage } from './filemanager-common.js';

declare const csrf: { fetch: typeof fetch };

let _client: GqlClient | null = null;
function getClient(): GqlClient {
	if (!_client) { _client = new GqlClient(csrf, '/gql'); }
	return _client;
}
/** Inject a custom GqlClient (primarily for tests). */
export function setGqlClient(client: GqlClient): void { _client = client; }
async function postGql<T = Record<string, unknown>>(query: string, variables?: Record<string, unknown>): Promise<GqlResponse<T>> {
	return getClient().execute<GqlResponse<T>>({ query, variables });
}

/** Element object interface */
interface ElementObject {
	get(): string;
	set(value: string): void;
	getNames(keys: string): string;
	getAll(): string;
	replace(all: string, names: string, keys: string): void;
	registerEvent(callback: () => void, event: string): void;
	isValid?: boolean;
	width?: number;
	height?: number;
	defaultFolder?: string;
}

/** Form object interface */
interface FormObject {
	getElement(name: string): ElementObject;
}

/** Media item instance interface */
interface MediaItemInstance {
	key: string;
	filename: string;
	description: string;
	size: number;
	folder: MediaFolderInstance | null;
	iconURL: string;
	url: string;
	isFolder: false;
	getPath(): string;
	getDAV(): string;
}

/** Media folder instance interface */
interface MediaFolderInstance {
	key: string;
	name: string;
	parent: MediaFolderInstance | null;
	folders: MediaFolderInstance[];
	media: MediaItemInstance[];
	isFolder: true;
	getPath(): string;
	getDAV(): string;
	addFolder(key: string, name: string): MediaFolderInstance;
	addMedia(key: string, filename: string, description: string, size: number, iconURL?: string): MediaItemInstance;
	findFolder(key: string): MediaFolderInstance | null;
	findMedia(key: string): MediaItemInstance | null;
}

/** Media listener instance interface */
interface MediaListenerInstance {
	id: string;
	key: string;
	mediaName: string | null;
	dav?: string;
	folderName?: string;
	getId(): string;
	getKey(): string;
	getMediaName(): string | null;
	listChanged(newList: MediaFolderInstance): void;
	valueSelected(mediaItem: MediaItemInstance | null): void;
	openLibrary(modal?: boolean, select?: boolean): void;
	isModal(): boolean;
	canSelect(): boolean;
	isSimple(): boolean;
	usesList(): boolean | null;
	usesSelected(): boolean;
	defaultFolder(): string | null;
}

/** Media listener manager instance interface */
interface MediaListenerManagerInstance {
	listeners: { [key: string]: MediaListenerInstance };
	addListener(listener: MediaListenerInstance): void;
	getListener(listenerId: string, libraryKey?: string): MediaListenerInstance | null;
	getListeners(libraryKey: string): MediaListenerInstance[];
	openLibrary(listener: MediaListenerInstance, modal?: boolean, select?: boolean): void;
}

/** Extension set type */
interface ExtensionSet {
	[key: string]: boolean;
}

var _MEDIA_JS_: boolean = true;

/**
 * Escape HTML to prevent XSS when inserting user-controlled content.
 */
function escapeHtmlMedia(text: string | null): string {
	if (text == null) return '';
	var div: HTMLDivElement = document.createElement('div');
	div.textContent = String(text);
	return div.innerHTML;
}

/**
 * Build media HTML for display.
 */
function buildMediaHTML(url: string, name: string | null): string {
	return buildMediaHTMLSizeable(url, name);
}

/**
 * Create a set from arguments.
 */
function toSet(...args: string[]): ExtensionSet {
	var i: number, out: ExtensionSet = {};
	for (i = 0; i < args.length; i++) out[args[i]] = true;
	return out;
}

var audioExts: ExtensionSet = toSet("aif", "aifc", "aiff", "au", "kar", "mid", "midi", "mp2", "mp3", "mpga", "ra", "ram", "rm", "rmi", "snd", "voc", "vox", "wav", "wma", "wmv");
var videoExts: ExtensionSet = toSet("asf", "asx", "avi", "mov", "movie", "mp2", "mp2v", "mp4", "mpa", "mpv2", "mpe", "mpeg", "mpg", "qt", "rm");
var imageExts: ExtensionSet = toSet("gif", "jpg", "jpe", "jpeg", "jfif", "png");
var officeExts: ExtensionSet = toSet("xls", "ppt", "doc");

/**
 * Build media HTML with size options.
 */
function buildMediaHTMLSizeable(url: string, name: string | null, width?: number, height?: number, bgcolor?: string): string {
	if (url == null) return '';
	var ext: string = getExtension(url);
	if (imageExts[ext])
		return '<img src="' + url + '"' + (name != null ? ' alt="' + escapeHtmlMedia(name) + '"' : '') + (width != null ? ' width="' + width + '"' : '') + (height != null ? ' height="' + height + '"' : '') + '>';
	if (ext == "swf") { // Flash
		if (bgcolor == null) bgcolor = "#FFFFFF";
		if (width == null) width = 300;
		if (height == null) height = 200;
		return '<object classid="clsid:D27CDB6E-AE6D-11cf-96B8-444553540000" '
			+ 'codebase="http://download.macromedia.com/pub/shockwave/cabs/flash/swflash.cab#version=5,0,30,0" width="' + width + '" '
			+ 'height="' + height + '"><param name="movie" value="' + url + '"><param name="loop" value="false"><param name="quality" '
			+ 'value="high"><param name="bgcolor" value="' + bgcolor + '"><param name="menu" value="false"><embed src="' + url + '" '
			+ 'quality="high" pluginspage="http://www.macromedia.com/shockwave/download/index.cgi?P1_Prod_Version=ShockwaveFlash" '
			+ 'type="application/x-shockwave-flash" width="' + width + '" height="' + width + '" menu="false" bgcolor="' + bgcolor + '"></embed></object>';
	}
	var isAudio: boolean = audioExts[ext], isVideo: boolean = videoExts[ext];
	if (isVideo) {
		if (width == null) width = 300;
		if (height == null) height = 200;
	}
	if (isAudio || isVideo) {
		return '<embed src="' + url + '"' + (width != null ? ' width="' + width + '"' : '') + (height != null ? ' height="' + height + '"' : '') + ' autoplay="false" loop="false" controller="true"></embed>';
	}
	return '<a href="' + url + '">' + (name != null ? escapeHtmlMedia(name) : 'Download File') + '</a>'; // Unknown
}

/**
 * Get file extension from URL.
 */
function getExtension(url: string): string {
	var qPos: number = url.indexOf('?'), e: number = (qPos == -1) ? url.length : qPos, b: number = url.lastIndexOf('.', e) + 1;
	return url.substring(b, e).toLowerCase();
}

/**
 * Open a DAV file.
 */
function openDAVFile(url: string): void {
	(window.location as any) = url;
}

/**
 * Get DAV URL for opening.
 */
function getOpenDAVURL(url: string): string {
	return url;
}

/**
 * Dynamically loads a JavaScript file and executes a callback when done.
 * Replaces $.getScript() functionality.
 */
function loadScript(url: string, callback: () => void): void {
	const script = document.createElement('script');
	script.src = url;
	script.onload = callback;
	script.onerror = () => {
		console.error('Failed to load script: ' + url);
	};
	document.head.appendChild(script);
}

// MediaItem class
function MediaItem(this: MediaItemInstance, key: string, filename: string, description: string, size: number, parent: MediaFolderInstance | null, iconURL: string): void {
	var t = this;
	t.key = key;
	t.filename = filename;
	t.description = description;
	t.size = size;
	t.folder = parent;
	t.iconURL = iconURL;
	t.url = APP_ROOT + "/download/" + key + "/" + filename;
	t.getPath = getPath_MediaItem;
	t.isFolder = false;
	t.getDAV = getDAV_MediaItem;
}

function getPath_MediaItem(this: MediaItemInstance): string {
	return this.folder != null ? this.folder.getPath() + this.filename : this.filename;
}

function getDAV_MediaItem(this: MediaItemInstance): string {
	return this.folder!.getDAV() + escape(this.filename);
}

// MediaFolder class
function MediaFolder(this: MediaFolderInstance, key: string, name: string | null, parent?: MediaFolderInstance | null): void {
	var t = this;
	t.key = key;
	t.name = name == null || name.length == 0 ? '/' : name;
	if (t.name.charAt(t.name.length - 1) != '/')
		t.name += "/";
	t.parent = parent || null;
	t.getPath = getPath_MediaFolder;
	t.folders = [];
	t.media = [];
	t.addFolder = addFolder_MediaFolder;
	t.addMedia = addMedia_MediaFolder;
	t.findFolder = findFolder_MediaFolder;
	t.findMedia = findMedia_MediaFolder;
	t.isFolder = true;
	t.getDAV = getDAV_MediaFolder;
}

function getPath_MediaFolder(this: MediaFolderInstance): string {
	return this.parent != null ? this.parent.getPath() + this.name : this.name;
}

function addFolder_MediaFolder(this: MediaFolderInstance, key: string, name: string): MediaFolderInstance {
	var mf: MediaFolderInstance = new (MediaFolder as any)(key, name, this);
	this.folders.push(mf);
	return mf;
}

function addMedia_MediaFolder(this: MediaFolderInstance, key: string, filename: string, description: string, size: number, iconURL?: string): MediaItemInstance {
	var mi: MediaItemInstance = new (MediaItem as any)(key, filename, description, size, this, iconURL);
	this.media.push(mi);
	return mi;
}

function findFolder_MediaFolder(this: MediaFolderInstance, key: string): MediaFolderInstance | null {
	if (this.key == key) return this;
	var list: MediaFolderInstance[] = this.folders;
	for (var i in list) {
		var item: MediaFolderInstance | null = list[i].findFolder(key);
		if (item != null) return item;
	}
	return null;
}

function findMedia_MediaFolder(this: MediaFolderInstance, key: string): MediaItemInstance | null {
	var list: MediaItemInstance[] = this.media;
	for (var i in list)
		if (list[i].key == key) return list[i];
	var folderList: MediaFolderInstance[] = this.folders;
	for (var j in folderList) {
		var item: MediaItemInstance | null = folderList[j].findMedia(key);
		if (item != null) return item;
	}
	return null;
}

/**
 * Clone a folder structure.
 */
function cloneFolder(oldFolder: MediaFolderInstance | null, newFolder?: MediaFolderInstance | null): MediaFolderInstance | null {
	if (oldFolder == null) return null;
	if (newFolder == null)
		newFolder = new (MediaFolder as any)(oldFolder.key, oldFolder.name);
	var list: MediaItemInstance[] = oldFolder.media;
	for (var i in list) {
		var item: MediaItemInstance = list[i];
		newFolder!.addMedia(item.key, item.filename, item.description, item.size);
	}
	var folderList: MediaFolderInstance[] = oldFolder.folders;
	for (var j in folderList) {
		var folderItem: MediaFolderInstance = folderList[j];
		var newItem: MediaFolderInstance = newFolder!.addFolder(folderItem.key, folderItem.name);
		cloneFolder(folderItem, newItem);
	}
	return newFolder!;
}

function getDAV_MediaFolder(this: MediaFolderInstance): string {
	var t = this;
	if (t.parent == null) {
		return "/files/" + t.key.substring(t.key.lastIndexOf("__") + 2) + "/";
	}
	return t.parent.getDAV() + escape(t.name);
}

// MediaListenerManager class
function MediaListenerManager(this: MediaListenerManagerInstance): void {
	var t = this;
	t.listeners = {};
	t.addListener = MediaListenerManager_addListener;
	t.getListener = MediaListenerManager_getListener;
	t.getListeners = MediaListenerManager_getListeners;
	t.openLibrary = MediaListenerManager_openLibrary;
}

var mediaListenerManager: MediaListenerManagerInstance = new (MediaListenerManager as any)();

function MediaListenerManager_addListener(this: MediaListenerManagerInstance, listener: MediaListenerInstance): void {
	this.listeners[listener.getId()] = listener;
}

function MediaListenerManager_getListener(this: MediaListenerManagerInstance, listenerId: string, libraryKey?: string): MediaListenerInstance | null {
	var listeners: MediaListenerInstance[], listener: MediaListenerInstance | null = this.listeners[listenerId] || null;
	if (listener == null && libraryKey != null) {
		listeners = this.getListeners(libraryKey);
		if (listeners.length == 1) listener = listeners[0];
	}
	return listener;
}

function MediaListenerManager_getListeners(this: MediaListenerManagerInstance, libraryKey: string): MediaListenerInstance[] {
	var i: string, list: MediaListenerInstance[] = [];
	for (i in this.listeners) {
		if (this.listeners[i].getKey() == libraryKey)
			list.push(this.listeners[i]);
	}
	return list;
}

function MediaListenerManager_openLibrary(this: MediaListenerManagerInstance, listener: MediaListenerInstance, modal?: boolean, select?: boolean): void {
	openMediaLibrary(listener, modal, select);
}

/**
 * Media callback for handling library changes and selections.
 */
function mediaCallback(libraryKey: string | null, mediaListKey: string | null, listenerId: string | null, mediaItemKey: string | null): void {
	if (libraryKey != null && mediaListKey != null) {
		var i: string, listeners: MediaListenerInstance[] = mediaListenerManager.getListeners(libraryKey);
		var build: boolean = false;
		for (i in listeners) {
			if (listeners[i].usesList() == true) {
				build = true;
			} else if (listeners[i].usesList() == null) {
				listeners[i].listChanged(mediaList);
			}
		}
		if (build) buildListAJAX(libraryKey);
	}
	if (listenerId != null && mediaItemKey != null) {
		var listener: MediaListenerInstance | null = mediaListenerManager.getListener(listenerId, libraryKey || undefined);
		if (listener != null && listener.usesSelected()) selectItemAJAX(libraryKey!, listenerId, mediaItemKey);
	}
}

/**
 * Build list via AJAX.
 */
function buildListAJAX(libraryKey: string): void {
	if (libraryKey > "" && libraryKey != "null") {
		loadScript("/shared/files/medialibrary_listjs.jsp?_id=" + libraryKey,
			function () {
				var i: string, listeners: MediaListenerInstance[] = mediaListenerManager.getListeners(libraryKey);
				for (i in listeners)
					if (listeners[i].usesList() == true) listeners[i].listChanged(mediaList);
			}
		);
	}
}

/**
 * Select item via AJAX.
 */
function selectItemAJAX(libraryKey: string, listenerId: string, mediaItemKey: string): void {
	if (libraryKey > "" && libraryKey != "null" && mediaItemKey > "" && mediaItemKey != "null") {
		loadScript("/shared/files/medialibrary_itemjs.jsp?_id=" + libraryKey + "&_id2=" + mediaItemKey,
			function () {
				var listener: MediaListenerInstance | null = mediaListenerManager.getListener(listenerId, libraryKey);
				if (listener) listener.valueSelected(mediaItem);
			}
		);
	}
}

// MediaListener base implementation
function MediaListener(this: MediaListenerInstance, id: string, libraryKey: string | null, mediaName?: string | null): void {
	var t = this;
	t.id = id;
	t.key = libraryKey || "";
	t.mediaName = mediaName || null;
	t.getId = MediaListener_getId;
	t.getKey = MediaListener_getKey;
	t.getMediaName = MediaListener_getMediaName;
	t.listChanged = MediaListener_listChanged;
	t.valueSelected = MediaListener_valueSelected;
	t.openLibrary = MediaListener_openLibrary;
	t.isModal = MediaListener_getFalse;
	t.canSelect = MediaListener_getFalse;
	t.isSimple = MediaListener_getFalse;
	t.usesList = MediaListener_getFalse;
	t.usesSelected = MediaListener_getFalse;
	t.defaultFolder = MediaListener_defaultFolder;
	mediaListenerManager.addListener(this);
}

function MediaListener_getId(this: MediaListenerInstance): string {
	return this.id;
}

function MediaListener_getKey(this: MediaListenerInstance): string {
	return (this.key == null || this.key.length == 0) ? MEDIA_LIBRARY_KEY : this.key;
}

function MediaListener_getMediaName(this: MediaListenerInstance): string | null {
	return this.mediaName;
}

function MediaListener_listChanged(this: MediaListenerInstance, newList: MediaFolderInstance): void {
	// by default, do nothing
}

function MediaListener_valueSelected(this: MediaListenerInstance, mediaItem: MediaItemInstance | null): void {
	// by default, do nothing
}

function MediaListener_openLibrary(this: MediaListenerInstance, modal?: boolean, select?: boolean): void {
	openMediaLibrary(this, modal, select);
}

function MediaListener_getFalse(): boolean { return false; }
function MediaListener_getTrue(): boolean { return true; }
function MediaListener_getNull(): null { return null; }

function MediaListener_defaultFolder(this: MediaListenerInstance): string | null {
	var t = this, end: number = t.dav ? t.dav.lastIndexOf("/") : -1, start: number = end > 0 ? t.dav!.indexOf("/", 8) : 0;
	return end > 0 && end > start ? t.dav!.substring(start, end) : (t.folderName || null);
}

// MediaSelectListener
interface MediaSelectListenerInstance extends MediaListenerInstance {
	element: ElementObject | null;
	keys: string;
	names: string;
	updateDAV(): void;
	addFolder(folder: MediaFolderInstance): void;
}

function MediaSelectListener(this: MediaSelectListenerInstance, selectMediaName: string, libraryKey: string | null, isSimple?: boolean, mediaName?: string | null): void {
	var t = this, firstFocus: boolean = true;
	MediaListener.call(t, selectMediaName, libraryKey, mediaName);
	t.element = getElementObj(selectMediaName);
	t.keys = "";
	t.names = "";
	t.updateDAV = function () {
		if (t.element && t.element.get() > "") selectItemAJAX(libraryKey || "", selectMediaName, t.element.get());
	};
	t.addFolder = MediaSelectListener_addFolder;
	t.canSelect = MediaListener_getTrue;
	if (isSimple)
		t.isSimple = MediaListener_getTrue;
	if (t.element != null) {
		t.listChanged = MediaSelectListener_listChanged;
		t.valueSelected = MediaSelectListener_valueSelected;
		t.usesList = MediaListener_getTrue;
		t.usesSelected = MediaListener_getTrue;
		t.element.registerEvent(t.updateDAV, "change");
		t.element.registerEvent(function () {
			if (firstFocus) {
				firstFocus = false;
				buildListAJAX(libraryKey || "");
			}
		}, "focus");
		t.updateDAV();
	}
}

function MediaSelectListener_listChanged(this: MediaSelectListenerInstance, newList: MediaFolderInstance): void {
	var i: string, t = this, e = t.element!, tempNS: string = NAME_SPACER;
	NAME_SPACER = "\n";
	t.keys = "", t.names = e.getNames(t.keys);  // find empty element
	t.addFolder(newList);
	var current: string = e.get();
	e.replace(e.getAll(), t.names, t.keys);
	e.set(current);
	NAME_SPACER = tempNS;
	t.updateDAV();
}

function MediaSelectListener_addFolder(this: MediaSelectListenerInstance, folder: MediaFolderInstance): void {
	var list: MediaItemInstance[] = folder.media;
	for (var i in list) {
		var item: MediaItemInstance = list[i];
		var path: string = item.getPath();
		if (path.length > 60) {
			path = path.substring(0, 10) + "..." + path.substring(path.length - 47);
		}
		this.names += NAME_SPACER + path;
		if (item.description != null && item.description.length > 0) {
			var desc: string = item.description;
			if (desc.length + path.length > 100) {
				desc = desc.substring(0, 97 - path.length) + "...";
			}
			this.names += " (" + desc + ")";
		}
		this.keys += VALUE_SPACER + item.key;
	}
	var folderList: MediaFolderInstance[] = folder.folders;
	for (var j in folderList) {
		var folderItem: MediaFolderInstance = folderList[j];
		this.addFolder(folderItem);
	}
}

function MediaSelectListener_valueSelected(this: MediaSelectListenerInstance, mediaItem: MediaItemInstance | null): void {
	if (mediaItem) {
		this.dav = mediaItem.getDAV();
		this.element!.set(mediaItem.key);
	}
}

// ZestyMediaListener
interface ZestyMediaListenerInstance extends MediaListenerInstance {
	zestyEditor: any;
}

function ZestyMediaListener(this: ZestyMediaListenerInstance, zesty: any): void {
	var t = this;
	MediaListener.call(t, zesty.id, zesty.settings.mediaKey, zesty.settings.mediaName);
	t.zestyEditor = zesty;
	t.valueSelected = ZestyMediaListener_valueSelected;
	t.usesSelected = MediaListener_getTrue;
	t.isModal = MediaListener_getTrue;
	t.canSelect = MediaListener_getTrue;
	if (zesty.settings.mediaSimple)
		t.isSimple = MediaListener_getTrue;
}

function ZestyMediaListener_valueSelected(this: ZestyMediaListenerInstance, mediaItem: MediaItemInstance | null): void {
	if (mediaItem && mediaItem.url != null) {
		this.zestyEditor.execCommand('mceBeginUndoLevel');
		this.zestyEditor.execCommand('mceInsertContent', false, buildMediaHTML(mediaItem.url, mediaItem.description > "" ? mediaItem.description : mediaItem.filename));
		this.zestyEditor.execCommand('mceEndUndoLevel');
	}
}

/**
 * Insert media into a zesty editor.
 */
function ZestyEditor_insertMedia(this: any, url: string | null, name: string | null): void {
	if (url != null) {
		this.insertHTML(buildMediaHTML(url, name));
	}
}

// AttachmentMediaListener
interface AttachmentMediaListenerInstance extends MediaListenerInstance {
	layerObj: HTMLElement | null;
}

function AttachmentMediaListener(
	this: AttachmentMediaListenerInstance,
	name: string = "attachments",
	key: string | null = getValue("mediaLibraryKey"),
	mediaName: string = "Attachments"
): void {
	var t = this;
	MediaListener.call(t, name, key, mediaName);
	t.layerObj = document.getElementById(name + "Div");
	t.listChanged = AttachmentMediaListener_listChanged;
	t.usesList = MediaListener_getTrue;
	t.isSimple = MediaListener_getTrue;
}

function AttachmentMediaListener_listChanged(this: AttachmentMediaListenerInstance, newList: MediaFolderInstance): void {
	var text: string = "",
		list: MediaItemInstance[] = newList.media;
	for (var i in list) {
		var item: MediaItemInstance = list[i];
		if (text > "") text += "; ";
		text += '<a href="' + item.url + '">' + escapeHtmlMedia(item.filename) + ' (' + parseInt(String(item.size / 1024)) + ' KB)</a>';
	}
	if (text == "") text = "<span class='inactive'>[<i>No Attachments</i>]</span>";
	if (this.layerObj) this.layerObj.innerHTML = text;
}

// URLPickerMediaListener
interface URLPickerMediaListenerInstance extends MediaListenerInstance {
	field: ElementObject;
	origValue: string;
	get(): string;
	fixLink(link: HTMLAnchorElement): void;
	goLink(): boolean;
	isValid(): boolean;
	editMedia(): void;
	getMediaKey(): string | null;
}

function URLPickerMediaListener(this: URLPickerMediaListenerInstance, form: string, field: string, key: string | null, dav?: string, folderName?: string): void {
	var t = this;
	MediaListener.call(t, form + "." + field, key);
	t.field = getFormObj(form).getElement(field);
	t.dav = dav;
	t.folderName = folderName;
	t.origValue = t.field.get();
	t.valueSelected = URLPickerMediaListener_valueSelected;
	t.listChanged = URLPickerMediaListener_listChanged;
	t.usesList = MediaListener_getNull;
	t.usesSelected = MediaListener_getTrue;
	t.isModal = MediaListener_getTrue;
	t.canSelect = MediaListener_getTrue;
	t.get = URLPickerMediaListener_get;
	t.fixLink = URLPickerMediaListener_fixLink;
	t.goLink = function () {
		var u: string = this.get();
		if (u.substring(0, 11) == "javascript:") {
			new Function(u.substring(11))();
		} else {
			(document.location as any) = u;
		}
		return false;
	};
	t.isValid = URLPickerMediaListener_isValid;
	t.editMedia = URLPickerMediaListener_editMedia;
	t.getMediaKey = URLPickerMediaListener_getMediaKey;
	t.getMediaName = URLPickerMediaListener_getMediaName_URL;
}

function URLPickerMediaListener_valueSelected(this: URLPickerMediaListenerInstance, mediaItem: MediaItemInstance | null): void {
	if (mediaItem == null) {
		this.field.set("");
	} else {
		this.field.set(mediaItem.url);
		this.dav = mediaItem.getDAV();
		this.origValue = mediaItem.url;
	}
}

function URLPickerMediaListener_listChanged(this: URLPickerMediaListenerInstance, newList: MediaFolderInstance): void {
	selectItemAJAX(this.getKey(), this.getId(), this.getMediaKey()!);
}

function URLPickerMediaListener_get(this: URLPickerMediaListenerInstance): string {
	var t = this;
	if (t.dav && t.dav > '' && t.field.get() == t.origValue) return getOpenDAVURL(t.dav);
	return t.isValid() ? t.field.get() : "javascript:alert('Invalid URL')";
}

function URLPickerMediaListener_fixLink(this: URLPickerMediaListenerInstance, link: HTMLAnchorElement): void {
	var url: string = this.get();
	link.href = url;
}

function URLPickerMediaListener_isValid(this: URLPickerMediaListenerInstance): boolean {
	return this.field.get() > "" && this.field.isValid === true;
}

function URLPickerMediaListener_editMedia(this: URLPickerMediaListenerInstance): void {
	var key: string | null = this.getMediaKey();
	if (key != null) {
		openMediaLibrary(this, null, true, null, key);
	} else {
		openMediaLibrary(this, null, true, null, null, this.folderName);
	}
}

function URLPickerMediaListener_getMediaKey(this: URLPickerMediaListenerInstance): string | null {
	var value: string = this.field.get();
	if (value.substring(0, 10) == "/download/") {
		var endKey: number = value.indexOf("/", 10);
		if (endKey > -1) {
			return value.substring(10, endKey);
		}
	}
	return null;
}

function URLPickerMediaListener_getMediaName_URL(): string {
	return "Record Files";
}

// DocLinkMediaListener
interface DocLinkMediaListenerInstance extends MediaListenerInstance {
	field: ElementObject;
	url: string;
	width: number | undefined;
	height: number | undefined;
	doThumb: boolean;
	layerObj: HTMLElement | null;
	uploadZone: HTMLElement | null;
	fileNameZone: HTMLElement | null;
	get(): string;
	fixLink(link: HTMLAnchorElement): void;
	isValid(): boolean;
	editMedia(): void;
	handleFileSelect(files: FileList): Promise<void>;
}

function DocLinkMediaListener(this: DocLinkMediaListenerInstance, form: string, field: string, key: string | null, url?: string): void {
	var t = this;
	MediaListener.call(t, form + "." + field, key);
	t.field = getFormObj(form).getElement(field);
	t.url = url || "";
	t.width = t.field.width;
	t.height = t.field.height;
	t.doThumb = t.width != null && t.width >= 16 && t.height != null && t.height >= 16;
	t.folderName = t.field.defaultFolder;
	t.layerObj = document.getElementById("doc_" + t.id);
	t.uploadZone = document.getElementById("docUploadZone_" + t.id);
	t.fileNameZone = document.getElementById("docFileName_" + t.id);
	t.valueSelected = DocLinkMediaListener_valueSelected;
	t.listChanged = DocLinkMediaListener_listChanged;
	t.usesList = MediaListener_getNull;
	t.usesSelected = MediaListener_getTrue;
	t.isModal = MediaListener_getTrue;
	t.canSelect = MediaListener_getTrue;
	t.get = DocLinkMediaListener_get;
	t.fixLink = DocLinkMediaListener_fixLink;
	t.isValid = DocLinkMediaListener_isValid;
	t.editMedia = DocLinkMediaListener_editMedia;
	t.handleFileSelect = DocLinkMediaListener_handleFileSelect;
	t.getMediaName = function () { return 'Record Files'; };
	if (t.field.get() > "") {
		// If server already rendered a link, just ensure correct visibility
		if (t.layerObj && t.layerObj.querySelector('a')) {
			if (t.uploadZone) t.uploadZone.style.display = 'none';
			if (t.fileNameZone) t.fileNameZone.style.display = '';
		} else {
			t.listChanged(null as any);
		}
	} else {
		t.valueSelected(null);
	}
}

function DocLinkMediaListener_valueSelected(this: DocLinkMediaListenerInstance, mediaItem: MediaItemInstance | null): void {
	var t = this;
	if (mediaItem == null) {
		if (t.field.get() != "") {
			t.field.set("");
		}
		if (t.layerObj) t.layerObj.innerHTML = '<i class="inactive">No file selected.</i>';
		// Show upload zone, hide file name zone
		if (t.uploadZone) t.uploadZone.style.display = '';
		if (t.fileNameZone) t.fileNameZone.style.display = 'none';
	} else {
		if (t.field.get() != mediaItem.key) {
			t.field.set(mediaItem.key);
		}
		t.dav = mediaItem.getDAV();
		t.url = mediaItem.url;
		var html: string = '<a href="' + t.get() + '" target="_new">';
		var url: string = mediaItem.iconURL;
		if (t.doThumb && url > "") {
			if (url.substring(0, 8) == "/images/") {
				if (t.width! >= 56 && t.height! >= 44) {
					// It's all good.
				} else if (t.width! >= 32 && t.height! >= 32) {
					url = addToFilename(url, "_32x32");
				} else {
					url = addToFilename(url, "_16x16");
				}
			} else {
				url = url.substring(0, url.lastIndexOf("?"));
				url += "?__w=" + t.width + "&__h=" + t.height;
			}
			html += '<img src="' + url + '" alt="' + escapeHtmlMedia(mediaItem.filename) + '" style="border:0;vertical-align:middle">';
		} else {
			html += escapeHtmlMedia(mediaItem.filename);
		}
		html += "</a>";
		if (t.layerObj) t.layerObj.innerHTML = html;
		// Hide upload zone, show file name zone
		if (t.uploadZone) t.uploadZone.style.display = 'none';
		if (t.fileNameZone) t.fileNameZone.style.display = '';
	}
}

function addToFilename(name: string, add: string): string {
	var pos: number = name.lastIndexOf('.');
	if (pos == -1) {
		return name + add;
	}
	return name.substring(0, pos) + add + name.substring(pos);
}

function DocLinkMediaListener_listChanged(this: DocLinkMediaListenerInstance, newList: MediaFolderInstance): void {
	selectItemAJAX(this.getKey(), this.getId(), this.field.get());
}

function DocLinkMediaListener_get(this: DocLinkMediaListenerInstance): string {
	var t = this;
	if (t.dav && t.dav > '') return getOpenDAVURL(t.dav);
	return t.isValid() ? t.url : "javascript:alert('No file selected.')";
}

function DocLinkMediaListener_fixLink(this: DocLinkMediaListenerInstance, link: HTMLAnchorElement): void {
	var url: string = this.get();
	link.href = url;
}

function DocLinkMediaListener_isValid(this: DocLinkMediaListenerInstance): boolean {
	return this.field.get() > "";
}

function DocLinkMediaListener_editMedia(this: DocLinkMediaListenerInstance): void {
	var key: string = this.field.get();
	if (key > "") {
		openMediaLibrary(this, null, true, null, key);
	} else {
		openMediaLibrary(this, null, true, null, null, this.folderName);
	}
}

async function DocLinkMediaListener_handleFileSelect(this: DocLinkMediaListenerInstance, files: FileList): Promise<void> {
	const t = this;
	if (!files || files.length === 0) return;
	const file: File = files[0];

	// Show uploading state
	if (t.layerObj) t.layerObj.innerHTML = '<i class="inactive">Uploading...</i>';
	if (t.uploadZone) t.uploadZone.style.display = 'none';
	if (t.fileNameZone) t.fileNameZone.style.display = '';

	try {
		// First get the root folder shortId and id via GraphQL
		const query: string = 'query GetLibraryFolder($id: String!) { remoteObject(id: $id) { id __typename ... on MediaLibraryInterface { folder { id shortId } } } }';
		const result = await postGql<{ remoteObject?: { folder?: { id: string; shortId: string } } }>(query, { id: t.getKey() });
		const rootFolder: any = result.data?.remoteObject?.folder;
		if (!rootFolder?.shortId) {
			DocLinkMediaListener_uploadFailed(t, isForbidden(result.errors)
				? 'Upload failed: ' + UPLOAD_FAILURE_REASONS[403]
				: 'Upload failed: Could not resolve the destination folder for this upload. Please try again, refresh this page, or contact support if the problem persists.');
			return;
		}

		// If there's a default folder configured, find or create it
		let targetFolder = rootFolder;
		if (t.folderName && t.folderName.length > 0) {
			const findQuery: string = 'query FindOrCreatePath($parentId: String!, $path: String!, $create: Boolean) { findPathElement(parentId: $parentId, path: $path, create: $create) { id shortId __typename } }';
			try {
				const findResult = await postGql<{ findPathElement?: { id: string; shortId: string } }>(findQuery, { parentId: rootFolder.id, path: t.folderName, create: true });
				const defaultFolder: any = findResult.data?.findPathElement;
				if (defaultFolder?.shortId) {
					targetFolder = defaultFolder;
				} else if (isForbidden(findResult.errors)) {
					// The refusal was about the root itself — resolving the default folder
					// has to read its parent first. Falling back to the root would re-ask
					// the question we were just told no to, and the PUT would come back a
					// bodyless 403 that reads to the user as nothing happening at all.
					DocLinkMediaListener_uploadFailed(t, 'Upload failed: ' + UPLOAD_FAILURE_REASONS[403]);
					return;
				} else {
					console.warn('[DocLinkMediaListener] Failed to find/create default folder, using root:', t.folderName, findResult.errors);
				}
			} catch (e) {
				console.warn('[DocLinkMediaListener] Failed to find/create default folder, using root:', t.folderName, e);
			}
		}

		await DocLinkMediaListener_uploadFile(t, file, targetFolder.shortId, targetFolder.id);
	} catch (e: any) {
		DocLinkMediaListener_uploadFailed(t, 'Upload failed: ' + (e?.message ?? String(e)));
	}
}

/**
 * A GraphQL refusal the server classified as `FORBIDDEN`. Branching on the typed
 * `extensions.code` is what it is for — matching on the message prose would break
 * the moment the wording changes.
 */
function isForbidden(errors: GqlError[] | undefined): boolean {
	return !!errors?.some(e => e.extensions?.code === 'FORBIDDEN');
}

/**
 * Restores the upload zone and shows `message` in place of the "Uploading..." state.
 * The message is rendered verbatim, so callers pass a complete sentence.
 *
 * Both zones stay visible: `layerObj` is rendered *inside* `fileNameZone`
 * (`<span class="docFileName"><span id="doc_…">`, see RelateTagShadow), so hiding
 * that zone — as the success and clear paths do — would take the message with it
 * and put us right back at a failure the user cannot see.
 */
function DocLinkMediaListener_uploadFailed(listener: DocLinkMediaListenerInstance, message: string): void {
	if (listener.layerObj) {
		listener.layerObj.innerHTML = '<i class="inactive">' + escapeHtml(message) + '</i>';
	}
	if (listener.fileNameZone) {
		listener.fileNameZone.style.display = '';
	}
	if (listener.uploadZone) {
		listener.uploadZone.style.display = '';
	}
	// Offering a retry means nothing while the input still holds the file that just
	// failed: picking the same one again fires no change event, so the drop zone
	// would look live and do nothing.
	const input = document.getElementById('docUpload_' + listener.id) as HTMLInputElement | null;
	if (input) {
		input.value = '';
	}
}

async function DocLinkMediaListener_uploadFile(listener: DocLinkMediaListenerInstance, file: File, shortId: string, folderId: string): Promise<void> {
	const webdavUrl: string = '/files/' + shortId + '/' + encodeURIComponent(file.name);

	const response: Response = await csrf.fetch(webdavUrl, {
		method: 'PUT',
		headers: { 'Content-Type': file.type || 'application/octet-stream' },
		body: file
	});

	if (!response.ok) {
		// statusText is empty over HTTP/2 and the server sends these bodyless, so
		// the raw value alone renders as "Upload failed:" with nothing after it.
		const body: string = await response.text().catch(() => '');
		DocLinkMediaListener_uploadFailed(listener,
			webdavFailureMessage('Upload', response.status, response.statusText, body, UPLOAD_FAILURE_REASONS));
		return;
	}

	// File uploaded - now query for the file to get its key
	await DocLinkMediaListener_selectUploadedFile(listener, folderId, file.name);
}

async function DocLinkMediaListener_selectUploadedFile(listener: DocLinkMediaListenerInstance, folderId: string, filename: string): Promise<void> {
	// Use findPathElement query to locate the uploaded file by path
	const query: string = 'query FindFile($parentId: String!, $path: String!) { findPathElement(parentId: $parentId, path: $path) { id __typename ... on DocumentInterface { documentName downloadURL iconURL } } }';

	let result: GqlResponse<{ findPathElement?: { id: string; documentName: string; downloadURL: string; iconURL: string } }>;
	try {
		result = await postGql(query, { parentId: folderId, path: filename });
	} catch (e: any) {
		if (listener.layerObj) listener.layerObj.innerHTML = '<i class="inactive">Error: ' + (e?.message ?? String(e)) + '</i>';
		return;
	}

	const pathElement: any = result.data?.findPathElement;
	if (pathElement?.id) {
		// Found the uploaded file - create a mediaItem and select it
		const mediaItem: MediaItemInstance = {
			key: pathElement.id,
			filename: pathElement.documentName,
			description: "",
			size: 0,
			folder: null,
			iconURL: pathElement.iconURL,
			url: pathElement.downloadURL,
			isFolder: false,
			getPath: function () { return this.filename; },
			getDAV: function () { return ''; }
		};
		listener.valueSelected(mediaItem);
	} else {
		// File not found - show error
		if (listener.layerObj) listener.layerObj.innerHTML = '<i class="inactive">Upload complete but file not found</i>';
	}
}

// Simple Document Upload helper functions (for DOCUMENT_UPLOAD type)
function docUploadSimpleSelect(input: HTMLInputElement, fieldName: string): void {
	if (input.files && input.files.length > 0) {
		var filename: string = input.files[0].name;
		var nameEl: HTMLElement | null = document.getElementById('doc_' + fieldName);
		var uploadZone: HTMLElement | null = document.getElementById('docUploadZone_' + fieldName);
		var fileNameZone: HTMLElement | null = document.getElementById('docFileName_' + fieldName);
		if (nameEl) nameEl.textContent = filename;
		if (uploadZone) uploadZone.style.display = 'none';
		if (fileNameZone) fileNameZone.style.display = '';
	}
}

function docUploadSimpleDrop(event: DragEvent, fieldName: string): void {
	var files: FileList | null = event.dataTransfer?.files || null;
	if (files && files.length > 0) {
		var input: HTMLInputElement | null = document.getElementById('docUpload_' + fieldName) as HTMLInputElement | null;
		if (input) {
			// Create a new DataTransfer and set the dropped files
			var dt: DataTransfer = new DataTransfer();
			dt.items.add(files[0]);
			input.files = dt.files;
			// Trigger the onchange handler
			docUploadSimpleSelect(input, fieldName);
		}
	}
}

function docUploadSimpleClear(fieldName: string): void {
	var input: HTMLInputElement | null = document.getElementById('docUpload_' + fieldName) as HTMLInputElement | null;
	var nameEl: HTMLElement | null = document.getElementById('doc_' + fieldName);
	var uploadZone: HTMLElement | null = document.getElementById('docUploadZone_' + fieldName);
	var fileNameZone: HTMLElement | null = document.getElementById('docFileName_' + fieldName);
	if (input) input.value = '';
	if (nameEl) nameEl.textContent = '';
	if (uploadZone) uploadZone.style.display = '';
	if (fileNameZone) fileNameZone.style.display = 'none';
}

// Listener Creation Methods
function addMediaSelectListener(listenerId: string, libraryKey: string | null, mediaName?: string | null): void {
	new (MediaSelectListener as any)(listenerId, libraryKey, mediaName);
}

function addZestyMediaListener(zestyEditor: any): void {
	new (ZestyMediaListener as any)(zestyEditor);
}

// Export to globalThis for backwards compatibility
(globalThis as any)._MEDIA_JS_ = _MEDIA_JS_;
(globalThis as any).escapeHtmlMedia = escapeHtmlMedia;
(globalThis as any).buildMediaHTML = buildMediaHTML;
(globalThis as any).toSet = toSet;
(globalThis as any).audioExts = audioExts;
(globalThis as any).videoExts = videoExts;
(globalThis as any).imageExts = imageExts;
(globalThis as any).officeExts = officeExts;
(globalThis as any).buildMediaHTMLSizeable = buildMediaHTMLSizeable;
(globalThis as any).getExtension = getExtension;
(globalThis as any).openDAVFile = openDAVFile;
(globalThis as any).getOpenDAVURL = getOpenDAVURL;
(globalThis as any).loadScript = loadScript;
(globalThis as any).MediaItem = MediaItem;
(globalThis as any).MediaFolder = MediaFolder;
(globalThis as any).cloneFolder = cloneFolder;
(globalThis as any).mediaListenerManager = mediaListenerManager;
(globalThis as any).MediaListenerManager = MediaListenerManager;
(globalThis as any).mediaCallback = mediaCallback;
(globalThis as any).buildListAJAX = buildListAJAX;
(globalThis as any).selectItemAJAX = selectItemAJAX;
(globalThis as any).MediaListener = MediaListener;
(globalThis as any).MediaSelectListener = MediaSelectListener;
(globalThis as any).ZestyMediaListener = ZestyMediaListener;
(globalThis as any).ZestyEditor_insertMedia = ZestyEditor_insertMedia;
(globalThis as any).AttachmentMediaListener = AttachmentMediaListener;
(globalThis as any).URLPickerMediaListener = URLPickerMediaListener;
(globalThis as any).DocLinkMediaListener = DocLinkMediaListener;
(globalThis as any).addToFilename = addToFilename;
(globalThis as any).docUploadSimpleSelect = docUploadSimpleSelect;
(globalThis as any).docUploadSimpleDrop = docUploadSimpleDrop;
(globalThis as any).docUploadSimpleClear = docUploadSimpleClear;
(globalThis as any).addMediaSelectListener = addMediaSelectListener;
(globalThis as any).addZestyMediaListener = addZestyMediaListener;

if (typeof _SR_ !== "undefined" && _SR_ !== null) {
	_SR_.notify("media.js");
}

// ES module exports
export {
	_MEDIA_JS_,
	escapeHtmlMedia,
	buildMediaHTML,
	toSet,
	audioExts,
	videoExts,
	imageExts,
	officeExts,
	buildMediaHTMLSizeable,
	getExtension,
	openDAVFile,
	getOpenDAVURL,
	loadScript,
	MediaItem,
	MediaFolder,
	cloneFolder,
	mediaListenerManager,
	MediaListenerManager,
	mediaCallback,
	buildListAJAX,
	selectItemAJAX,
	MediaListener,
	MediaSelectListener,
	ZestyMediaListener,
	ZestyEditor_insertMedia,
	AttachmentMediaListener,
	URLPickerMediaListener,
	DocLinkMediaListener,
	addToFilename,
	docUploadSimpleSelect,
	docUploadSimpleDrop,
	docUploadSimpleClear,
	addMediaSelectListener,
	addZestyMediaListener
};
export type {
	MediaItemInstance,
	MediaFolderInstance,
	MediaListenerInstance,
	MediaListenerManagerInstance,
	MediaSelectListenerInstance,
	ZestyMediaListenerInstance,
	AttachmentMediaListenerInstance,
	URLPickerMediaListenerInstance,
	DocLinkMediaListenerInstance,
	ExtensionSet
};
